<template>
  <div class="speaking-video-detail">
    <!-- Video Container -->
    <div class="video-container">
      <!-- Video File -->
      <video
        v-if="videoUrl"
        width="100%"
        height="600"
        controls
        style="background-color: #000"
      >
        <source :src="videoUrl" :type="videoMimeType" />
        Your browser does not support the video tag.
      </video>

      <!-- No Video -->
      <div v-else class="no-video">
        <p>Không có video nào để phát</p>
      </div>
    </div>

    <!-- Video Details Table -->
    <div class="table-container" v-if="videoData">
      <v-card>
        <v-card-title>Video Details</v-card-title>
        <v-card-text>
          <v-layout wrap style="padding: 10px 0; margin-bottom: 20px;">
            <v-flex xs12 sm6 md4>
              <div class="info-label">User ID:</div>
              <div class="info-value">{{ videoData.userId || '—' }}</div>
            </v-flex>
            <v-flex xs12 sm6 md4>
              <div class="info-label">Status:</div>
              <div class="info-value">{{ getDisplayStatus(videoData.status) || '—' }}</div>
            </v-flex>
            <v-flex xs12 sm6 md4>
              <div class="info-label">Topic:</div>
              <div class="info-value">{{ videoData.topic || '—' }}</div>
            </v-flex>
            <v-flex xs12 sm6 md4>
              <div class="info-label">Sent Time:</div>
              <div class="info-value">{{ formatDateTime(videoData.uploadedAt) }}</div>
            </v-flex>
            <v-flex xs12 sm6 md4>
              <div class="info-label">Text Feedback Time:</div>
              <div class="info-value">{{ formatDateTime(videoData.feedbackTextCreatedAt) }}</div>
            </v-flex>
            <v-flex xs12 sm6 md4>
              <div class="info-label">Audio Feedback Time:</div>
              <div class="info-value">{{ formatDateTime(videoData.feedbackAudioCreatedAt) }}</div>
            </v-flex>
          </v-layout>

          <!-- Feedback Text -->
          <div class="feedback-section">
            <div class="section-title">Feedback Text:</div>
            <div v-if="!isEditing" class="feedback-text">{{ videoData.feedbackText || '—' }}</div>
            <v-textarea
              v-else
              v-model="editedData.feedbackText"
              :maxlength="500"
              :counter="500"
              rows="3"
              auto-grow
              hide-details
              dense
              no-resize
            ></v-textarea>
          </div>

          <!-- Feedback Audio -->
          <div class="feedback-section">
            <div class="section-title">Feedback Audio:</div>
            <div v-if="!isEditing">
              <div v-if="videoData.feedbackAudio">
                <audio
                  :src="getAudioUrl(videoData.feedbackAudio)"
                  controls
                  style="width: 100%; max-width: 500px;"
                  @loadedmetadata="onAudioLoaded"
                  @canplay="onAudioCanPlay"
                ></audio>
              </div>
              <div v-else>—</div>
            </div>
            <div v-else>
              <template v-if="isRecording">
                <v-btn
                  small
                  color="error"
                  @click="stopRecording"
                >
                  <v-icon left>stop</v-icon>
                  Stop Recording
                </v-btn>
              </template>
              <template v-else-if="editedData.feedbackAudio">
                <span>{{ editedData.feedbackAudio }}</span>
                <v-btn
                  icon
                  small
                  color="error"
                  @click="deleteRecord"
                >
                  <v-icon small>close</v-icon>
                </v-btn>
                <v-btn
                  small
                  color="primary"
                  @click="startRecording"
                >
                  <v-icon left>mic</v-icon>
                  Record
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  small
                  color="primary"
                  @click="startRecording"
                >
                  <v-icon left>mic</v-icon>
                  Record Audio
                </v-btn>
              </template>
            </div>
          </div>

          <!-- Topic Selection -->
          <div class="feedback-section">
            <div class="section-title">Topic:</div>
            <div v-if="!isEditing">{{ videoData.topic || '—' }}</div>
            <v-autocomplete
              v-else
              v-model="editedData.topicId"
              :items="topicList"
              :search-input.sync="topicSearch"
              item-text="name"
              item-value="name"
              single-line
              hide-details
              dense
              clearable
              autofocus
              no-data-text="No topic found"
              placeholder="Search topic..."
              browser-autocomplete="off"
              @update:search-input="onTopicSearch"
            ></v-autocomplete>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <template v-if="!isEditing">
              <v-btn
                color="primary"
                @click="startEdit"
                class="action-btn"
              >
                <v-icon left>edit</v-icon>
                Edit
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                color="success"
                @click="saveEdit"
                class="action-btn"
              >
                <v-icon left>save</v-icon>
                Save
              </v-btn>
              <v-btn
                color="error"
                @click="cancelEdit"
                class="action-btn"
              >
                <v-icon left>cancel</v-icon>
                Cancel
              </v-btn>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import { getSpeakingVideoDetail, submitFeedback } from '@/api/speakingVideoAPI';
import axios from 'axios';
import { message } from '@/shared/message';
import moment from "moment/moment";

export default {
  name: 'SpeakingVideoDetail',
  data() {
    return {
      videoUrl: null,
      videoData: null,
      loading: false,
      isEditing: false,
      editedData: {
        feedbackText: '',
        feedbackAudio: '',
        topicId: ''
      },
      originalData: {},
      topicList: [],
      topicSearch: '',
      topicSearchTimeout: null,
      isRecording: false,
      mediaRecorder: null,
      audioStream: null,
      audioChunks: [],
      audioBlob: null,
      recordCounter: 1,
      headers: [
        { text: 'User ID', value: 'userId', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Feedback Text', value: 'feedbackText', sortable: false, width: '300px' },
        { text: 'Feedback Audio', value: 'feedbackAudio', sortable: false, width: '320px' },
        { text: 'Topic', value: 'topic', sortable: false },
        { text: 'Sent Time', value: 'uploadedAt', sortable: false },
        { text: 'Text Feedback Time', value: 'feedbackTextCreatedAt', sortable: false },
        { text: 'Audio Feedback Time', value: 'feedbackAudioCreatedAt', sortable: false },
        { text: 'Actions', value: 'action', sortable: false, align: 'center', width: '100px' },
      ]
    }
  },
  computed: {
    videoMimeType() {
      if (!this.videoUrl) return 'video/mp4'
      
      if (this.videoUrl.endsWith('.mov')) {
        return 'video/quicktime'
      } else if (this.videoUrl.endsWith('.mp4')) {
        return 'video/mp4'
      } else if (this.videoUrl.endsWith('.webm')) {
        return 'video/webm'
      } else if (this.videoUrl.endsWith('.ogg')) {
        return 'video/ogg'
      }
      
      return 'video/mp4' // default fallback
    }
  },
  created() {
    this.fetchVideoDetail();
  },
  watch: {
    '$route.query': function() {
      this.fetchVideoDetail();
    }
  },
  mounted() {
    this.fetchTopics();
  },
  methods: {
    formatDateTime(timestamp) {
      if (!timestamp) return '—';
      return moment(timestamp).format('HH:mm:ss DD/MM/YYYY');
    },
    getDisplayStatus(status) {
      // Convert UPLOADED to PENDING for display
      if (status === 'UPLOADED') {
        return 'PENDING';
      }
      return status;
    },
    getAudioUrl(audioPath) {
      if (!audioPath) {
        return '';
      }
      // If already a full URL, return as is
      if (audioPath.startsWith('http://') || audioPath.startsWith('https://')) {
        return audioPath;
      }
      // If starts with /, it's a relative path, add base URL
      if (audioPath.startsWith('/')) {
        return 'https://static.babilala.vn' + audioPath;
      }
      // Otherwise, assume it's already a full path or return as is
      return audioPath;
    },
    onAudioLoaded(event) {
      const audio = event.target;
      audio.controls = true;
      audio.style.pointerEvents = 'auto';
      audio.style.opacity = '1';
    },
    onAudioCanPlay(event) {
      const audio = event.target;
      audio.controls = true;
      audio.style.pointerEvents = 'auto';
      audio.style.opacity = '1';
    },
    async fetchVideoDetail() {
      const itemId = this.$route.query.itemId;
      const videoUrlParam = this.$route.query.video_url;
      
      this.loading = true;
      try {
        if (itemId) {
          const response = await getSpeakingVideoDetail(itemId);
          this.videoData = response;
          // Nếu response có dữ liệu và có videoUrl, thì dùng response.videoUrl
          if (response && response.videoUrl) {
            this.videoUrl = response.videoUrl;
          } else {
            // Còn lại, dùng param video_url
            this.videoUrl = videoUrlParam ? decodeURIComponent(videoUrlParam) : null;
          }
        } else {
          // Nếu không có itemId, dùng trực tiếp param video_url
          this.videoUrl = videoUrlParam ? decodeURIComponent(videoUrlParam) : null;
        }
      } catch (error) {
        console.error('Error fetching video detail:', error);
        // Khi lỗi, fallback về param video_url
        this.videoUrl = videoUrlParam ? decodeURIComponent(videoUrlParam) : null;
      } finally {
        this.loading = false;
      }
    },
    async fetchTopics(search = '') {
      try {
        const params = search ? { search: search } : {};
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/topic`, { params });
        this.topicList = response || [];
      } catch (error) {
        console.error('Error fetching topics:', error);
        this.topicList = [];
      }
    },
    onTopicSearch(search) {
      // Clear existing timeout
      if (this.topicSearchTimeout) {
        clearTimeout(this.topicSearchTimeout);
      }
      
      // Debounce search - wait 300ms after user stops typing
      this.topicSearchTimeout = setTimeout(() => {
        if (search !== null && search !== undefined) {
          this.fetchTopics(search);
        } else {
          // If search is cleared, fetch all topics
          this.fetchTopics();
        }
      }, 300);
    },
    getFileName(filePath) {
      if (!filePath) return '';
      // Extract filename from path (handle both / and \ separators)
      const lastSlash = Math.max(filePath.lastIndexOf('/'), filePath.lastIndexOf('\\'));
      return lastSlash !== -1 ? filePath.substring(lastSlash + 1) : filePath;
    },
    buildExpectedFeedbackAudioUrl(fileName) {
      if (!fileName) return '';
      if (fileName.startsWith('http://') || fileName.startsWith('https://')) return fileName;
      const base = String(fileName).replace(/\.(webm|mp3)$/i, '');
      return `https://static.babilala.vn/uploads/kidnew/record-feedback/${base}.mp3`;
    },
    startEdit() {
      this.isEditing = true;
      this.originalData = {
        feedbackText: this.videoData.feedbackText || '',
        feedbackAudio: this.videoData.feedbackAudio || '',
        topicId: this.videoData.topic || ''
      };
      this.editedData = {
        feedbackText: this.videoData.feedbackText || '',
        feedbackAudio: this.getFileName(this.videoData.feedbackAudio || ''),
        topicId: this.videoData.topic || ''
      };
      // Initialize record counter
      this.recordCounter = 0;
      if (this.videoData.feedbackAudio) {
        const fileName = this.getFileName(this.videoData.feedbackAudio);
        // Extract counter from filename: record_{videoId}_{counter}.ext
        const match = fileName.match(/_(\d+)\.(webm|mp3)$/);
        if (match && match[1]) {
          this.recordCounter = parseInt(match[1], 10);
        }
      }
      // Reset audio state
      this.audioBlob = null;
      this.audioChunks = [];
      if (this.mediaRecorder) {
        this.mediaRecorder = null;
      }
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
        this.audioStream = null;
      }
      this.isRecording = false;
      // Load all topics
      this.topicSearch = '';
      this.fetchTopics();
    },
    cancelEdit() {
      // Stop recording if in progress
      if (this.isRecording && this.mediaRecorder) {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
        this.audioStream = null;
      }
      // Clear search timeout if exists
      if (this.topicSearchTimeout) {
        clearTimeout(this.topicSearchTimeout);
        this.topicSearchTimeout = null;
      }
      this.topicSearch = '';
      this.isEditing = false;
      this.editedData = {
        feedbackText: '',
        feedbackAudio: '',
        topicId: ''
      };
      this.originalData = {};
      this.audioChunks = [];
      this.mediaRecorder = null;
      this.audioBlob = null;
    },
    async startRecording() {
      try {
        const videoId = this.videoData.videoId || this.videoData.id;
        
        // Stop existing recording if any
        if (this.isRecording && this.mediaRecorder) {
          this.mediaRecorder.stop();
          this.isRecording = false;
        }
        if (this.audioStream) {
          this.audioStream.getTracks().forEach(track => track.stop());
          this.audioStream = null;
        }
        
        // Increment counter
        this.recordCounter++;
        const currentRecordNumber = this.recordCounter;
        console.log('startRecording - videoId:', videoId, 'recordCounter:', currentRecordNumber);
        
        // Delete existing record name
        this.editedData.feedbackAudio = '';
        this.audioChunks = [];
        
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.audioStream = stream;
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];
        
        // Store videoId in closure for onstop callback
        const videoIdForCallback = videoId;
        const recordNumberForCallback = currentRecordNumber;
        
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };
        
        this.mediaRecorder.onstop = () => {
          this.audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
          const fileName = `record_${videoIdForCallback}_${recordNumberForCallback}.webm`;
          this.editedData.feedbackAudio = fileName;
          console.log('Recording stopped - Set editedData.feedbackAudio to:', fileName);
          
          // Stop all tracks to release microphone
          if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
          }
          this.isRecording = false;
        };
        
        this.mediaRecorder.start();
        this.isRecording = true;
      } catch (error) {
        console.error('Error accessing microphone:', error);
        message.error('Không thể truy cập microphone. Vui lòng kiểm tra quyền truy cập.');
        this.isRecording = false;
        if (this.audioStream) {
          this.audioStream.getTracks().forEach(track => track.stop());
          this.audioStream = null;
        }
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
    },
    deleteRecord() {
      this.editedData.feedbackAudio = '';
      this.audioChunks = [];
      this.audioBlob = null;
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
        this.audioStream = null;
      }
    },
    async saveEdit() {
      try {
        // Capture values IMMEDIATELY
        const audioBlobToSave = this.audioBlob;
        const audioFileNameToSave = String(this.editedData.feedbackAudio || '');
        
        console.log('saveEdit - audioBlob exists:', !!audioBlobToSave);
        console.log('saveEdit - audioFileName (captured):', audioFileNameToSave);
        console.log('saveEdit - editedData:', JSON.stringify(this.editedData));
        
        let audioFile = null;
        if (audioBlobToSave && audioFileNameToSave) {
          audioFile = new File([audioBlobToSave], audioFileNameToSave, { type: 'audio/webm' });
          console.log('Created File object with name:', audioFile.name);
        } else {
          console.warn('Cannot create audio file - audioBlob:', !!audioBlobToSave, 'fileName:', audioFileNameToSave);
        }

        const response = await submitFeedback(
          this.videoData.id,
          this.editedData.topicId || null,
          this.editedData.feedbackText || null,
          audioFile
        );
        
        if (response && response.success) {
          // Update local videoData
          const now = Date.now();
          this.videoData.topicId = this.editedData.topicId || null;
          this.videoData.topic = this.editedData.topicId || null;
          this.videoData.feedbackText = this.editedData.feedbackText || null;
          if (this.editedData.feedbackText) {
            this.videoData.feedbackTextCreatedAt = now;
          }
          // Only update audio when we actually uploaded a new file
          if (audioFile && audioFileNameToSave) {
            this.videoData.feedbackAudio = this.buildExpectedFeedbackAudioUrl(audioFileNameToSave);
            this.videoData.feedbackAudioCreatedAt = now;
          }
          this.videoData.status = 'REVIEWED';
          
          message.success('Cập nhật thành công');
          this.cancelEdit();
        } else {
          message.error(response?.data?.description || 'Cập nhật thất bại');
        }
      } catch (error) {
        console.error('Error saving feedback:', error);
        message.error('Có lỗi xảy ra khi cập nhật');
      }
    }
  }
}
</script>

<style scoped>
.speaking-video-detail {
  padding: 20px;
}

.video-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 30px;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.no-video {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 600px;
  color: #999;
  font-size: 18px;
}

.table-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.info-label {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  margin-bottom: 5px;
}

.info-value {
  color: #666;
  font-size: 14px;
  word-break: break-word;
}

.feedback-section {
  margin-top: 20px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
}

.section-title {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  margin-bottom: 10px;
}

.feedback-text {
  color: #666;
  font-size: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.5;
}

/* Ensure audio controls are visible */
audio {
  pointer-events: auto !important;
  opacity: 1 !important;
  cursor: pointer !important;
}

.action-buttons {
  margin-top: 30px;
  display: flex;
  gap: 10px;
  justify-content: flex-start;
}

.action-btn {
  margin-right: 10px !important;
}
</style>
